import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import {
  Google,
  Close,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

interface AuthModalProps {
  open: boolean;
  onClose: () => void;
}

const AuthModal: React.FC<AuthModalProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { login, register, loginWithGoogle, resetPassword } = useAuth();

  const [mode, setMode] = useState<'login' | 'register' | 'reset'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (mode === 'register') {
        if (password !== confirmPassword) {
          throw new Error('Les mots de passe ne correspondent pas');
        }
        if (password.length < 6) {
          throw new Error('Le mot de passe doit contenir au moins 6 caractères');
        }
        await register(email, password);
        setSuccess('Compte créé avec succès !');
        setTimeout(() => onClose(), 1500);
      } else if (mode === 'login') {
        await login(email, password);
        setSuccess('Connexion réussie !');
        setTimeout(() => onClose(), 1000);
      } else if (mode === 'reset') {
        await resetPassword(email);
        setSuccess('Email de réinitialisation envoyé !');
        setMode('login');
      }
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    try {
      await loginWithGoogle();
      setSuccess('Connexion avec Google réussie !');
      setTimeout(() => onClose(), 1000);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la connexion avec Google');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
    setShowPassword(false);
  };

  const switchMode = (newMode: 'login' | 'register' | 'reset') => {
    setMode(newMode);
    resetForm();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          m: isMobile ? 0 : 2,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" component="h2">
          {mode === 'login' && 'Connexion'}
          {mode === 'register' && 'Créer un compte'}
          {mode === 'reset' && 'Réinitialiser le mot de passe'}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            sx={{ mb: 2 }}
            disabled={loading}
          />

          {mode !== 'reset' && (
            <TextField
              fullWidth
              label="Mot de passe"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              sx={{ mb: 2 }}
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
          )}

          {mode === 'register' && (
            <TextField
              fullWidth
              label="Confirmer le mot de passe"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              sx={{ mb: 2 }}
              disabled={loading}
            />
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            sx={{ 
              mb: 2, 
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '12px',
              boxShadow: '0 8px 20px rgba(102, 126, 234, 0.4)',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                boxShadow: '0 12px 25px rgba(102, 126, 234, 0.6)',
                transform: 'translateY(-2px)',
              },
              '&:active': {
                transform: 'translateY(0px)',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
              }
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <>
                {mode === 'login' && '🔐 Se connecter'}
                {mode === 'register' && '✨ Créer le compte'}
                {mode === 'reset' && '📧 Envoyer l\'email'}
              </>
            )}
          </Button>

          {mode !== 'reset' && (
            <>
              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  ou
                </Typography>
              </Divider>

              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<Google />}
                onClick={handleGoogleLogin}
                disabled={loading}
                sx={{ 
                  mb: 2, 
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  background: 'linear-gradient(135deg, #4285f4 0%, #34a853 100%)',
                  color: 'white',
                  borderRadius: '12px',
                  border: 'none',
                  boxShadow: '0 6px 16px rgba(66, 133, 244, 0.4)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #3367d6 0%, #2d8f47 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 10px 25px rgba(66, 133, 244, 0.6)',
                  },
                  '&:active': {
                    transform: 'translateY(0px)',
                    boxShadow: '0 6px 16px rgba(66, 133, 244, 0.4)',
                  }
                }}
              >
                🚀 Continuer avec Google
              </Button>
            </>
          )}

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            {mode === 'login' && (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Pas encore de compte ?{' '}
                  <Button
                    variant="text"
                    size="medium"
                    onClick={() => switchMode('register')}
                    disabled={loading}
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '0.95rem',
                      color: '#667eea',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(102, 126, 234, 0.1)',
                        transform: 'scale(1.05)',
                      }
                    }}
                  >
                    ✨ S'inscrire
                  </Button>
                </Typography>
                <Button
                  variant="text"
                  size="medium"
                  onClick={() => switchMode('reset')}
                  disabled={loading}
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    color: '#f59e0b',
                    padding: '6px 12px',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(245, 158, 11, 0.1)',
                      transform: 'scale(1.05)',
                    }
                  }}
                >
                  🔑 Mot de passe oublié ?
                </Button>
              </>
            )}

            {mode === 'register' && (
              <Typography variant="body2">
                Déjà un compte ?{' '}
                <Button
                  variant="text"
                  size="medium"
                  onClick={() => switchMode('login')}
                  disabled={loading}
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '0.95rem',
                    color: '#10b981',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(16, 185, 129, 0.1)',
                      transform: 'scale(1.05)',
                    }
                  }}
                >
                  🔐 Se connecter
                </Button>
              </Typography>
            )}

            {mode === 'reset' && (
              <Button
                variant="text"
                size="medium"
                onClick={() => switchMode('login')}
                disabled={loading}
                sx={{
                  fontWeight: 'bold',
                  fontSize: '0.95rem',
                  color: '#6366f1',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(99, 102, 241, 0.1)',
                    transform: 'scale(1.05)',
                  }
                }}
              >
                ← Retour à la connexion
              </Button>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
